// Lookup Slice - Redux state management for lookup data (regions, super regions, roles)

import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { LookupService } from "../../api/services";

// Async thunk to fetch regions
export const fetchRegions = createAsyncThunk(
  "lookup/fetchRegions",
  async (_, { rejectWithValue }) => {
    try {
      const response = await LookupService.getRegions();
      return response?.data || response || [];
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Async thunk to fetch super regions by region
export const fetchSuperRegionsByRegion = createAsyncThunk(
  "lookup/fetchSuperRegionsByRegion",
  async (regionGuid, { rejectWithValue }) => {
    try {
      const response = await LookupService.getSuperRegionsByRegion(regionGuid);
      return { regionGuid, data: response?.data || response || [] };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Async thunk to fetch roles by region
export const fetchRolesByRegion = createAsyncThunk(
  "lookup/fetchRolesByRegion",
  async (regionGuid, { rejectWithValue }) => {
    try {
      const response = await LookupService.getRolesByRegion(regionGuid);
      return { regionGuid, data: response?.data || response || [] };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

const initialState = {
  regions: [],
  regionsLoading: false,
  regionsError: null,
  
  // Store super regions and roles by regionGuid for caching
  superRegionsByRegion: {}, // { [regionGuid]: [...superRegions] }
  superRegionsLoading: false,
  superRegionsError: null,
  
  rolesByRegion: {}, // { [regionGuid]: [...roles] }
  rolesLoading: false,
  rolesError: null,
};

const lookupSlice = createSlice({
  name: "lookup",
  initialState,
  reducers: {
    clearLookupData: (state) => {
      state.regions = [];
      state.superRegionsByRegion = {};
      state.rolesByRegion = {};
    },
  },
  extraReducers: (builder) => {
    // Fetch regions
    builder
      .addCase(fetchRegions.pending, (state) => {
        state.regionsLoading = true;
        state.regionsError = null;
      })
      .addCase(fetchRegions.fulfilled, (state, action) => {
        state.regionsLoading = false;
        state.regions = action.payload;
      })
      .addCase(fetchRegions.rejected, (state, action) => {
        state.regionsLoading = false;
        state.regionsError = action.payload;
      });

    // Fetch super regions by region
    builder
      .addCase(fetchSuperRegionsByRegion.pending, (state) => {
        state.superRegionsLoading = true;
        state.superRegionsError = null;
      })
      .addCase(fetchSuperRegionsByRegion.fulfilled, (state, action) => {
        state.superRegionsLoading = false;
        state.superRegionsByRegion[action.payload.regionGuid] = action.payload.data;
      })
      .addCase(fetchSuperRegionsByRegion.rejected, (state, action) => {
        state.superRegionsLoading = false;
        state.superRegionsError = action.payload;
      });

    // Fetch roles by region
    builder
      .addCase(fetchRolesByRegion.pending, (state) => {
        state.rolesLoading = true;
        state.rolesError = null;
      })
      .addCase(fetchRolesByRegion.fulfilled, (state, action) => {
        state.rolesLoading = false;
        state.rolesByRegion[action.payload.regionGuid] = action.payload.data;
      })
      .addCase(fetchRolesByRegion.rejected, (state, action) => {
        state.rolesLoading = false;
        state.rolesError = action.payload;
      });
  },
});

export const { clearLookupData } = lookupSlice.actions;

// Selectors
export const selectRegions = (state) => state.lookup.regions;
export const selectRegionsLoading = (state) => state.lookup.regionsLoading;
export const selectSuperRegionsByRegion = (state, regionGuid) => 
  state.lookup.superRegionsByRegion[regionGuid] || [];
export const selectRolesByRegion = (state, regionGuid) => 
  state.lookup.rolesByRegion[regionGuid] || [];
export const selectSuperRegionsLoading = (state) => state.lookup.superRegionsLoading;
export const selectRolesLoading = (state) => state.lookup.rolesLoading;

export default lookupSlice.reducer;
